import { useStore } from "@nanostores/react"
import { flatMap } from "lodash"
import { stringify } from "qs"
import { memo, useMemo, useCallback } from "react"
import { numberRegExp } from "./constant"
import { useValidateSearch } from "./hook/search"
import { v4 as uuidv4 } from "uuid"
import { useAsset, useDecimal, useUser } from "./service/service"
import AssetIcon from "./component/asset_icon"
import { Spinner } from "./component/spinner"
import { paramAtom, userAtom, assetAtom, decimalAtom, amountAtom, memoAtom, useAmount, useMemoString } from "./atom/atom"
import { Param } from "./type/param"
import { Transaction } from "./component/transaction"
import { ItemBackground } from "./component/item_background"
import { Input } from "./component/input"
import { ErrorMessage } from "./component/error"

export const Index = memo(() => {
  const { data, intersectedKey, errors } = useValidateSearch(Param)

  const constraints = useMemo(() => flatMap(errors.map((item) => Object.values(item.constraints || {}))), [errors])

  useMemo(() => {
    paramAtom.set(data)
  }, [data])

  if (!intersectedKey.length) {
    window.location.href = "https://mixin.one/"
    return <></>
  }

  if (intersectedKey.length && constraints.length) {
    return <ErrorMessage>Please check the link, parameters are wrong.</ErrorMessage>
  }

  return <Home />
})

const Home = memo(() => {
  const { user_id, asset_id } = useStore(paramAtom)

  const { value: user, error: userError } = useUser(user_id)
  const { value: asset, error: assetError } = useAsset(user_id, asset_id)
  const decimal = useDecimal(asset?.asset_key)

  useMemo(() => {
    if (!user) return
    userAtom.set(user)
  }, [user])

  useMemo(() => {
    if (!asset) return
    assetAtom.set(asset)
  }, [asset])

  useMemo(() => {
    decimalAtom.set(decimal)
  }, [decimal])

  if (userError || assetError) {
    return <ErrorMessage>Please check the link or network, information is not available.</ErrorMessage>
  }

  if (!user || !asset) {
    return (
      <div className=" flex h-screen w-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="mt-16 p-4">
      <div className=" flex w-full flex-col items-center rounded-lg border border-slate-200 px-7 pt-10 pb-7">
        {!user.avatar_url && <p className="font-semibold">Pay to {user.full_name}</p>}
        <Asset className=" mt-4" />
        <div className="mt-4 flex w-full flex-col gap-2">
          <ItemBackground className="flex items-center gap-3">
            <img src={user.avatar_url} alt="avatar" className="rounded-full" width={30} height={30} />
            <p className="font-semibold">{user.full_name}</p>
          </ItemBackground>
          <Amount />
          <Memo />
          <MixinButton />
          <Transaction />
        </div>
      </div>
    </div>
  )
})

const Asset = memo<{ className?: string }>(({ className }) => {
  const { icon_url, chain_icon_url, symbol } = useStore(assetAtom)
  return (
    <>
      <AssetIcon assetIconUrl={icon_url} chainIconUrl={chain_icon_url} iconSize={40} chainIconSize={16} chainClassName="border rounded-full border-white" className={`${className ? className : ""}`} />
      <p className="mt-2">{symbol}</p>
    </>
  )
})

const Amount = memo(() => {
  const { amount: initAmount } = useStore(paramAtom)
  const amount = useStore(amountAtom)
  const { symbol } = useStore(assetAtom)
  const onChange = useCallback((value) => {
    if (value && !value.match(numberRegExp)) return
    return amountAtom.set(value)
  }, [])

  return <Input title="Amount" placeholder="Please enter an amount" postfix={symbol} autoFocus value={initAmount || amount} disabled={!!initAmount} onChange={onChange} />
})

const Memo = memo(() => {
  const { memo: initMemo } = useStore(paramAtom)
  const memo = useStore(memoAtom)
  const onChange = useCallback((value) => {
    if (value.length > 139) return
    return memoAtom.set(value)
  }, [])

  return <Input title="Memo" placeholder="Please enter a memo" value={initMemo || memo} disabled={!!initMemo} onChange={onChange} />
})

const MixinButton = memo(() => {
  const { user_id, asset_id, trace } = useStore(paramAtom)
  const amount = useAmount()
  const memo = useMemoString()

  const query = useMemo(() => {
    if (!amount) return

    return stringify(
      {
        recipient: user_id,
        asset: asset_id,
        amount,
        memo,
        trace: trace || uuidv4(),
      },
      {
        skipNulls: true,
      }
    )
  }, [user_id, asset_id, amount, memo, trace])

  if (!amount) return <></>

  return (
    <a className="mt-10 flex h-12 w-full items-center justify-center rounded-md bg-primary text-white" href={`https://mixin.one/pay?${query}`}>
      Use Mixin
    </a>
  )
})
